import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Edit2, Trash2, Lock, CheckCircle2, AlertCircle, Download, Upload } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip';
import ReportWorkflowTrail from '../../reports/ReportWorkflowTrail';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ChartOfAccountsTab({ onRefresh, isReadOnly = false }) {
    const fileInputRef = useRef(null);
    const [accounts, setAccounts] = useState([]);
    const [filteredAccounts, setFilteredAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [showLocked, setShowLocked] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingAccount, setEditingAccount] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [lastResult, setLastResult] = useState(null);
    
    const [form, setForm] = useState({
        account_code: '',
        account_name: '',
        account_type: '',
        normal_balance: '',
        sub1_type: 'None',
        sub2_type: 'None',
        is_active: true
    });
    
    const ACCOUNT_TYPES = ['Asset', 'Liability', 'Equity', 'Income', 'Expense', 'Contra-Asset', 'Contra-Liability'];
    const SUB_TYPES = ['None', 'Customer', 'Supplier', 'Branch', 'Project'];
    const IMPORT_HEADERS = ['account_code', 'account_name', 'account_type', 'normal_balance', 'parent_code', 'sub1_type', 'sub2_type', 'is_active'];
    
    // Fetch accounts
    const fetchAccounts = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/accounts?include_locked=${showLocked}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAccounts(response.data);
            setLoading(false);
        } catch (error) {
            toast.error('Failed to load accounts');
            setLoading(false);
        }
    }, [showLocked]);
    
    useEffect(() => {
        fetchAccounts();
    }, [fetchAccounts]);
    
    // Filter accounts based on search
    useEffect(() => {
        let filtered = accounts;
        
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(acc => 
                acc.account_code?.toLowerCase().includes(query) ||
                acc.account_name?.toLowerCase().includes(query)
            );
        }
        
        setFilteredAccounts(filtered);
    }, [accounts, searchQuery]);
    
    // Auto-set normal balance based on type
    const handleTypeChange = (type) => {
        setForm(prev => ({
            ...prev,
            account_type: type,
            normal_balance: ['Asset', 'Expense', 'Contra-Liability'].includes(type) ? 'Dr' : 'Cr'
        }));
    };

    const escapeCSV = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

    const downloadTemplate = () => {
        const rows = [
            IMPORT_HEADERS,
            ['1010', 'Cash - New Branch', 'Asset', 'Dr', '', 'None', 'None', 'true'],
            ['1010-01', 'Cash on Hand - New Branch', 'Asset', 'Dr', '1010', 'None', 'None', 'true'],
            ['2020', 'Accounts Payable - Vendors', 'Liability', 'Cr', '', 'Supplier', 'None', 'true'],
        ];
        const csv = rows.map(row => row.map(escapeCSV).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `macesuite-coa-import-template-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success('COA import template downloaded. This is a file download only; no accounts, GL, or Trial Balance records changed.');
    };

    const parseCsvLine = (line) => {
        const values = [];
        let current = '';
        let inQuotes = false;

        for (let i = 0; i < line.length; i += 1) {
            const char = line[i];
            const nextChar = line[i + 1];

            if (char === '"' && inQuotes && nextChar === '"') {
                current += '"';
                i += 1;
            } else if (char === '"') {
                inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
                values.push(current.trim());
                current = '';
            } else {
                current += char;
            }
        }

        values.push(current.trim());
        return values;
    };

    const parseBoolean = (value) => {
        const normalized = String(value ?? '').trim().toLowerCase();
        if (!normalized) return true;
        return ['true', 'yes', 'y', '1', 'active'].includes(normalized);
    };

    const parseCsvAccounts = (text) => {
        const lines = text
            .replace(/^\uFEFF/, '')
            .split(/\r?\n/)
            .map(line => line.trim())
            .filter(Boolean);

        if (lines.length < 2) {
            throw new Error('CSV must include a header row and at least one account row');
        }

        const headers = parseCsvLine(lines[0]).map(header => header.trim().toLowerCase());
        const missingHeaders = ['account_code', 'account_name', 'account_type'].filter(header => !headers.includes(header));
        if (missingHeaders.length > 0) {
            throw new Error(`Missing required column(s): ${missingHeaders.join(', ')}`);
        }

        return lines.slice(1).map((line) => {
            const values = parseCsvLine(line);
            const raw = {};
            headers.forEach((header, index) => {
                raw[header] = values[index] ?? '';
            });

            return {
                account_code: raw.account_code || '',
                account_name: raw.account_name || '',
                account_type: raw.account_type || '',
                normal_balance: raw.normal_balance || null,
                parent_code: raw.parent_code || null,
                sub1_type: raw.sub1_type || 'None',
                sub2_type: raw.sub2_type || 'None',
                is_active: parseBoolean(raw.is_active),
            };
        });
    };

    const handleImportClick = () => {
        if (isReadOnly) {
            toast.error('Chart of Accounts is read-only in this review scope.');
            return;
        }
        fileInputRef.current?.click();
    };

    const handleImportFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;

        if (isReadOnly) {
            toast.error('Chart of Accounts is read-only in this review scope.');
            return;
        }

        if (!file.name.toLowerCase().endsWith('.csv')) {
            toast.error('Please choose a CSV file');
            return;
        }

        setImporting(true);
        setImportResult(null);
        try {
            const text = await file.text();
            const rows = parseCsvAccounts(text);
            const token = localStorage.getItem('token');
            const response = await axios.post(`${API}/accounts/import`, { rows }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setImportResult({
                ok: true,
                message: response.data.message,
                created_count: response.data.created_count,
                skipped_count: response.data.skipped_count,
                skipped: response.data.skipped || [],
                errors: []
            });
            setLastResult({
                title: response.data.message || 'COA import completed',
                status: 'Imported',
                source: `${response.data.created_count || 0} account(s) created, ${response.data.skipped_count || 0} skipped`,
                reference: file.name,
                location: 'Setup > Chart of Accounts and account dropdowns in JV, AR, AP, Bank Reconciliation, GL, and reports.',
                reviewState: 'Review account type, normal balance, subaccount requirements, and report classification before posting transactions.',
                glImpact: 'COA setup only. No GL, Trial Balance, or financial report amount changes until posted transactions use these accounts.',
                nextSafeAction: 'Search the imported account codes, then lock stable accounts or correct setup before students post work.'
            });
            toast.success(
                `${response.data.message} Find new accounts in Setup > Chart of Accounts and account dropdowns. No GL or Trial Balance impact until used by posted transactions.`
            );
            fetchAccounts();
            if (onRefresh) onRefresh();
        } catch (error) {
            const detail = error.response?.data?.detail;
            const errors = Array.isArray(detail?.errors) ? detail.errors : [];
            const message = detail?.message || error.message || 'Failed to import CSV';
            setImportResult({
                ok: false,
                message,
                created_count: 0,
                skipped_count: 0,
                skipped: [],
                errors
            });
            toast.error(message);
        } finally {
            setImporting(false);
        }
    };
    
    // Reset form
    const resetForm = () => {
        setForm({
            account_code: '',
            account_name: '',
            account_type: '',
            normal_balance: '',
            sub1_type: 'None',
            sub2_type: 'None',
            is_active: true
        });
        setIsEditing(false);
        setEditingAccount(null);
        setShowForm(false);
    };
    
    // Handle Add button
    const handleAdd = () => {
        if (isReadOnly) {
            toast.error('Chart of Accounts is read-only in this review scope.');
            return;
        }
        resetForm();
        setShowForm(true);
    };
    
    // Handle Edit button
    const handleEdit = async (account) => {
        if (isReadOnly) {
            toast.error('Chart of Accounts is read-only in this review scope.');
            return;
        }
        // Check if account is used in posted transactions
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/accounts/${account.account_code}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setForm({
                account_code: response.data.account_code,
                account_name: response.data.account_name,
                account_type: response.data.account_type,
                normal_balance: response.data.normal_balance,
                sub1_type: response.data.sub1_type || 'None',
                sub2_type: response.data.sub2_type || 'None',
                is_active: response.data.is_active
            });
            setEditingAccount(response.data);
            setIsEditing(true);
            setShowForm(true);
        } catch (error) {
            toast.error('Failed to load account details');
        }
    };
    
    // Handle Save
    const handleSave = async () => {
        if (isReadOnly) {
            toast.error('Chart of Accounts is read-only in this review scope.');
            return;
        }
        // Validation
        if (!form.account_code.trim()) {
            toast.error('Account Code is required');
            return;
        }
        if (!form.account_name.trim()) {
            toast.error('Account Name is required');
            return;
        }
        if (!form.account_type) {
            toast.error('Account Type is required');
            return;
        }
        
        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            
            if (isEditing) {
                await axios.put(`${API}/accounts/${form.account_code}`, {
                    account_name: form.account_name,
                    account_type: form.account_type,
                    normal_balance: form.normal_balance,
                    sub1_type: form.sub1_type,
                    sub2_type: form.sub2_type,
                    is_active: form.is_active
                }, { headers: { Authorization: `Bearer ${token}` } });
                setLastResult({
                    title: `Account ${form.account_code} updated`,
                    status: form.is_active ? 'Active' : 'Inactive',
                    source: form.account_name,
                    reference: `${form.account_type} / ${form.normal_balance}`,
                    location: 'Setup > Chart of Accounts and account dropdowns in JV, AR, AP, Bank Reconciliation, GL, and reports.',
                    reviewState: 'Available for account selection according to status, lock state, and permissions.',
                    glImpact: 'COA setup only. Existing posted GL and Trial Balance amounts are not recalculated by this edit.',
                    nextSafeAction: 'Confirm report classification and subaccount requirements before posting transactions with this account.'
                });
                toast.success(
                    `Account ${form.account_code} updated. Find it in Setup > Chart of Accounts and account dropdowns. No GL or Trial Balance amount changed by this setup edit.`
                );
            } else {
                await axios.post(`${API}/accounts`, form, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setLastResult({
                    title: `Account ${form.account_code} created`,
                    status: form.is_active ? 'Active' : 'Inactive',
                    source: form.account_name,
                    reference: `${form.account_type} / ${form.normal_balance}`,
                    location: 'Setup > Chart of Accounts and account dropdowns in JV, AR, AP, Bank Reconciliation, GL, and reports.',
                    reviewState: 'Available for account selection according to status, lock state, and permissions.',
                    glImpact: 'COA setup only. No GL, Trial Balance, or financial report amount changes until posted transactions use this account.',
                    nextSafeAction: 'Confirm report classification and subaccount requirements before students post transactions with this account.'
                });
                toast.success(
                    `Account ${form.account_code} created. Find it in Setup > Chart of Accounts and account dropdowns. No GL or Trial Balance impact until used by posted transactions.`
                );
            }
            
            resetForm();
            fetchAccounts();
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save account');
        } finally {
            setSaving(false);
        }
    };
    
    // Handle Delete
    const handleDelete = async (account) => {
        if (isReadOnly) {
            toast.error('Chart of Accounts is read-only in this review scope.');
            return;
        }
        if (!window.confirm(`Are you sure you want to delete account ${account.account_code}?`)) {
            return;
        }
        
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API}/accounts/${account.account_code}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLastResult({
                title: `Account ${account.account_code} deleted`,
                status: 'Removed',
                source: account.account_name,
                reference: `${account.account_type} / ${account.normal_balance}`,
                location: 'Removed from Setup > Chart of Accounts if the backend confirmed it is safe to delete.',
                reviewState: 'Unavailable for new transaction selection after refresh.',
                glImpact: 'COA setup only. Backend controls protect accounts already used by posted transactions.',
                nextSafeAction: 'Refresh related account dropdowns and use a replacement account only after confirming report classification.'
            });
            toast.success(
                `Account ${account.account_code} deleted from Chart of Accounts. Backend controls protect accounts already used by posted transactions; no GL or Trial Balance amount changed.`
            );
            fetchAccounts();
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete account');
        }
    };
    
    // Handle Lock
    const handleLock = async (account) => {
        if (isReadOnly) {
            toast.error('Chart of Accounts is read-only in this review scope.');
            return;
        }
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API}/accounts/${account.account_code}/lock`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLastResult({
                title: `Account ${account.account_code} locked`,
                status: 'Locked',
                source: account.account_name,
                reference: `${account.account_type} / ${account.normal_balance}`,
                location: 'Setup > Chart of Accounts when Show Locked is enabled.',
                reviewState: 'Locked setup should be treated as protected structure for future transactions.',
                glImpact: 'COA lock status only. Posted GL, Trial Balance, and reports remain unchanged.',
                nextSafeAction: 'Keep locked if this account structure is stable; unlock only for controlled setup correction.'
            });
            toast.success(
                `Account ${account.account_code} locked. It remains in Chart of Accounts and account evidence; posted GL and Trial Balance amounts are unchanged.`
            );
            fetchAccounts();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to lock account');
        }
    };
    
    // Handle Unlock
    const handleUnlock = async (account) => {
        if (isReadOnly) {
            toast.error('Chart of Accounts is read-only in this review scope.');
            return;
        }
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API}/accounts/${account.account_code}/unlock`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLastResult({
                title: `Account ${account.account_code} unlocked`,
                status: account.is_active ? 'Active' : 'Inactive',
                source: account.account_name,
                reference: `${account.account_type} / ${account.normal_balance}`,
                location: 'Setup > Chart of Accounts and account dropdowns according to active status.',
                reviewState: 'Editable again for controlled setup correction.',
                glImpact: 'COA lock status only. Posted GL, Trial Balance, and reports remain unchanged.',
                nextSafeAction: 'Review the account setup immediately, then lock it again if the structure is stable.'
            });
            toast.success(
                `Account ${account.account_code} unlocked for controlled setup correction. Posted GL and Trial Balance amounts are unchanged.`
            );
            fetchAccounts();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to unlock account');
        }
    };
    
    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-[#2F8FCB] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    
    return (
        <div className="space-y-4" data-testid="coa-tab">
            {isReadOnly && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900" data-testid="coa-readonly-scope">
                    Chart of Accounts is read-only in this review scope. Use it to confirm account setup, normal balances, and report mapping; changes should be made only from an editable setup workspace.
                </div>
            )}

            <ReportWorkflowTrail
                title="Chart of Accounts trail"
                testId="coa-workflow-trail"
                scope={isReadOnly ? 'Read-only review' : 'Your setup workspace'}
                items={[
                    {
                        label: 'Source setup',
                        value: 'Accounts define posting choices, normal balances, subaccount requirements, and report classification.'
                    },
                    {
                        label: 'Where it goes',
                        value: 'Active accounts appear in Setup > Chart of Accounts plus JV, AR, AP, Bank Reconciliation, GL, and report filters.'
                    },
                    {
                        label: 'Review',
                        value: 'Confirm account type, normal balance, active/locked status, and customer/supplier subaccount requirements before posting.'
                    },
                    {
                        label: 'GL and TB',
                        value: 'COA setup alone does not affect GL or Trial Balance; posted transactions using these accounts do.'
                    },
                    {
                        label: 'Next safe action',
                        value: 'Lock stable accounts and use corrections carefully because posted evidence depends on consistent account structure.'
                    }
                ]}
            />

            {lastResult && (
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-950" data-testid="coa-result-trail">
                    <div className="flex flex-col gap-1 md:flex-row md:items-start md:justify-between">
                        <div>
                            <p className="font-semibold">{lastResult.title}</p>
                            <p className="mt-1 text-indigo-800">Status: <span className="font-semibold">{lastResult.status}</span></p>
                        </div>
                        <span className="w-fit rounded-full bg-white px-3 py-1 text-xs font-semibold text-indigo-700">
                            Latest setup action
                        </span>
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                        <p><span className="font-semibold">Created/changed:</span> {lastResult.source}</p>
                        <p><span className="font-semibold">Reference:</span> {lastResult.reference}</p>
                        <p><span className="font-semibold">Find it now:</span> {lastResult.location}</p>
                        <p><span className="font-semibold">Review:</span> {lastResult.reviewState}</p>
                        <p><span className="font-semibold">Accounting impact:</span> {lastResult.glImpact}</p>
                        <p><span className="font-semibold">Next safe action:</span> {lastResult.nextSafeAction}</p>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-slate-900 font-['Manrope']">Chart of Accounts</h2>
                    <p className="text-sm text-slate-500">Manage your account structure</p>
                </div>
                <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm text-slate-600">
                        <input
                            type="checkbox"
                            checked={showLocked}
                            onChange={(e) => setShowLocked(e.target.checked)}
                            className="rounded border-slate-300"
                            data-testid="show-locked-accounts"
                        />
                        Show Locked
                    </label>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={downloadTemplate}
                        data-testid="download-coa-template-btn"
                    >
                        <Download className="w-4 h-4 mr-2" /> Template
                    </Button>
                    {!isReadOnly && (
                        <>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleImportClick}
                                disabled={importing}
                                data-testid="import-coa-btn"
                            >
                                <Upload className="w-4 h-4 mr-2" /> {importing ? 'Importing...' : 'Import CSV'}
                            </Button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv,text/csv"
                                onChange={handleImportFile}
                                className="hidden"
                                data-testid="import-coa-file-input"
                            />
                            <Button
                                onClick={handleAdd}
                                className="bg-[#2F8FCB] hover:bg-[#2577a8]"
                                data-testid="add-account-btn"
                            >
                                <Plus className="w-4 h-4 mr-2" /> Add Account
                            </Button>
                        </>
                    )}
                </div>
            </div>
            
            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                    placeholder="Search by Account Code or Name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="coa-search"
                />
            </div>

            {importResult && (
                <div
                    className={`border rounded-lg p-4 text-sm ${
                        importResult.ok
                            ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                            : 'bg-red-50 border-red-200 text-red-900'
                    }`}
                    data-testid="coa-import-result"
                >
                    <div className="flex items-start gap-2">
                        {importResult.ok ? (
                            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        ) : (
                            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        )}
                        <div>
                            <p className="font-semibold">{importResult.message}</p>
                            {importResult.ok && (
                                <p className="mt-1">
                                    Created: {importResult.created_count} account(s). Skipped existing: {importResult.skipped_count} account(s).
                                </p>
                            )}
                            {importResult.skipped?.length > 0 && (
                                <ul className="mt-2 list-disc pl-5">
                                    {importResult.skipped.slice(0, 5).map(item => (
                                        <li key={`${item.row}-${item.account_code}`}>
                                            Row {item.row}: {item.account_code} skipped ({item.reason})
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {importResult.errors?.length > 0 && (
                                <ul className="mt-2 list-disc pl-5" data-testid="coa-import-errors">
                                    {importResult.errors.slice(0, 8).map((item, index) => (
                                        <li key={`${item.row}-${item.field}-${index}`}>
                                            Row {item.row}, {item.field}: {item.message}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            )}
            
            {/* Account Form Dialog */}
            <Dialog open={showForm} onOpenChange={setShowForm}>
                <DialogContent className="max-w-lg" aria-describedby="account-form-description">
                    <DialogHeader>
                        <DialogTitle>{isEditing ? 'Edit Account' : 'Add New Account'}</DialogTitle>
                    </DialogHeader>
                    <p id="account-form-description" className="sr-only">
                        Form to {isEditing ? 'edit an existing' : 'create a new'} chart of accounts entry
                    </p>
                    
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Account Code *</Label>
                                <Input
                                    value={form.account_code}
                                    onChange={(e) => setForm(prev => ({ ...prev, account_code: e.target.value }))}
                                    disabled={isEditing}
                                    placeholder="e.g., 1000"
                                    data-testid="account-code-input"
                                />
                            </div>
                            <div>
                                <Label>Account Type *</Label>
                                <Select value={form.account_type} onValueChange={handleTypeChange}>
                                    <SelectTrigger data-testid="account-type-select">
                                        <SelectValue placeholder="Select type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ACCOUNT_TYPES.map(type => (
                                            <SelectItem key={type} value={type}>{type}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        <div>
                            <Label>Account Name *</Label>
                            <Input
                                value={form.account_name}
                                onChange={(e) => setForm(prev => ({ ...prev, account_name: e.target.value }))}
                                placeholder="e.g., Cash on Hand"
                                data-testid="account-name-input"
                            />
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <Label>Normal Balance</Label>
                                <Select value={form.normal_balance} onValueChange={(v) => setForm(prev => ({ ...prev, normal_balance: v }))}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Auto" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Dr">Debit (Dr)</SelectItem>
                                        <SelectItem value="Cr">Credit (Cr)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Sub1 Type</Label>
                                <Select value={form.sub1_type} onValueChange={(v) => setForm(prev => ({ ...prev, sub1_type: v }))}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {SUB_TYPES.map(type => (
                                            <SelectItem key={type} value={type}>{type}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Sub2 Type</Label>
                                <Select value={form.sub2_type} onValueChange={(v) => setForm(prev => ({ ...prev, sub2_type: v }))}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {SUB_TYPES.map(type => (
                                            <SelectItem key={type} value={type}>{type}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="is_active"
                                checked={form.is_active}
                                onChange={(e) => setForm(prev => ({ ...prev, is_active: e.target.checked }))}
                                className="rounded border-slate-300"
                            />
                            <Label htmlFor="is_active" className="cursor-pointer">Active</Label>
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={resetForm} disabled={saving}>
                            Cancel
                        </Button>
                        <Button onClick={handleSave} disabled={saving} className="bg-[#2F8FCB] hover:bg-[#2577a8]">
                            {saving ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Accounts Grid */}
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                        <tr>
                            <th className="text-left px-4 py-3">Account Code</th>
                            <th className="text-left px-4 py-3">Account Name</th>
                            <th className="text-left px-4 py-3">Type</th>
                            <th className="text-center px-4 py-3">Normal Bal.</th>
                            <th className="text-center px-4 py-3">Sub1 Req.</th>
                            <th className="text-center px-4 py-3">Sub2 Req.</th>
                            <th className="text-center px-4 py-3">Status</th>
                            <th className="text-center px-4 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {filteredAccounts.length === 0 ? (
                            <tr>
                                <td colSpan="8" className="text-center py-8 text-slate-500">
                                    No accounts found
                                </td>
                            </tr>
                        ) : (
                            filteredAccounts.map((acc) => (
                                <tr key={acc.account_code} className={`hover:bg-slate-50 ${acc.is_locked ? 'bg-amber-50' : ''} ${!acc.is_active ? 'opacity-50' : ''}`}>
                                    <td className="px-4 py-3 font-mono font-medium">{acc.account_code}</td>
                                    <td className="px-4 py-3">{acc.account_name}</td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                            acc.account_type === 'Asset' ? 'bg-blue-100 text-blue-700' :
                                            acc.account_type === 'Liability' ? 'bg-red-100 text-red-700' :
                                            acc.account_type === 'Equity' ? 'bg-purple-100 text-purple-700' :
                                            acc.account_type === 'Income' || acc.account_type === 'Revenue' ? 'bg-green-100 text-green-700' :
                                            acc.account_type === 'Expense' ? 'bg-amber-100 text-amber-700' :
                                            'bg-slate-100 text-slate-700'
                                        }`}>
                                            {acc.account_type}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <span className={`font-medium ${acc.normal_balance === 'Dr' ? 'text-blue-600' : 'text-green-600'}`}>
                                            {acc.normal_balance}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        {acc.sub1_required ? (
                                            <span className="text-emerald-600">✓ {acc.sub1_type}</span>
                                        ) : (
                                            <span className="text-slate-400">-</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        {acc.sub2_required ? (
                                            <span className="text-emerald-600">✓ {acc.sub2_type}</span>
                                        ) : (
                                            <span className="text-slate-400">-</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        {acc.is_locked ? (
                                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                                                <Lock className="w-3 h-3 inline mr-1" />Locked
                                            </span>
                                        ) : acc.is_active ? (
                                            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">Active</span>
                                        ) : (
                                            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs font-medium">Inactive</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center justify-center gap-1">
                                            {isReadOnly ? (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button
                                                            className="p-1.5 text-slate-300 cursor-not-allowed rounded"
                                                            disabled
                                                            data-testid={`readonly-account-${acc.account_code}`}
                                                        >
                                                            <Lock className="w-4 h-4" />
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>Read-only review</TooltipContent>
                                                </Tooltip>
                                            ) : !acc.is_locked && (
                                                <>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <button
                                                                onClick={() => handleEdit(acc)}
                                                                className="p-1.5 hover:bg-blue-100 rounded text-blue-600"
                                                                data-testid={`edit-${acc.account_code}`}
                                                            >
                                                                <Edit2 className="w-4 h-4" />
                                                            </button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>Edit</TooltipContent>
                                                    </Tooltip>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <button
                                                                onClick={() => handleDelete(acc)}
                                                                className="p-1.5 hover:bg-red-100 rounded text-red-600"
                                                                data-testid={`delete-${acc.account_code}`}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>Delete</TooltipContent>
                                                    </Tooltip>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <button
                                                                onClick={() => handleLock(acc)}
                                                                className="p-1.5 hover:bg-amber-100 rounded text-amber-600"
                                                                data-testid={`lock-${acc.account_code}`}
                                                            >
                                                                <Lock className="w-4 h-4" />
                                                            </button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>Lock</TooltipContent>
                                                    </Tooltip>
                                                </>
                                            )}
                                            {!isReadOnly && acc.is_locked && (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button
                                                            onClick={() => handleUnlock(acc)}
                                                            className="p-1.5 hover:bg-emerald-100 rounded text-emerald-600"
                                                            data-testid={`unlock-${acc.account_code}`}
                                                        >
                                                            <CheckCircle2 className="w-4 h-4" />
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>Unlock</TooltipContent>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            
            {/* Summary */}
            <div className="flex items-center justify-between text-sm text-slate-500 px-1">
                <span>Showing {filteredAccounts.length} of {accounts.length} accounts</span>
                <span className="flex items-center gap-4">
                    <span className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-emerald-500 rounded-full"></span> Active
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-amber-500 rounded-full"></span> Locked
                    </span>
                </span>
            </div>
        </div>
    );
}

// ================= JOURNAL VOUCHER TAB =================


export default ChartOfAccountsTab;
